import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Download, FileText, TrendingUp, Users, Trophy, Calendar,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Promoter performance panel — shared by Admin + Organizer dashboards.
 *
 * Shows: aggregate leaderboard across the selected scope, per-event rollups,
 * and CSV / PDF download buttons. Server strictly redacts Unight margins so
 * the organizer view and the admin view show the same economics.
 */
export default function PromoterAnalyticsPanel({ token, events, defaultLastN = 5 }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastN, setLastN] = useState(defaultLastN);
  const [eventId, setEventId] = useState('');
  const [downloading, setDownloading] = useState('');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = eventId ? { event_id: eventId } : { last_n_events: lastN };
      const res = await axios.get(`${API}/analytics/promoters`, { headers: authHeaders, params });
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
      setReport(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, eventId, lastN]);

  useEffect(() => { load(); }, [load]);

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const params = eventId ? { event_id: eventId } : { last_n_events: lastN };
      const res = await axios.get(`${API}/analytics/promoters/export.${kind}`, {
        headers: authHeaders,
        params,
        responseType: 'blob',
      });
      const mime = kind === 'csv' ? 'text/csv' : 'application/pdf';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 12);
      a.download = `unight-promoter-report-${stamp}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(`${kind.toUpperCase()} download failed`);
    } finally {
      setDownloading('');
    }
  };

  const aggregates = report?.aggregates || [];
  const perEvent = report?.events || [];
  const top = aggregates[0];

  return (
    <div className="space-y-6" data-testid="promoter-analytics-panel">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 border border-zinc-800 bg-[#050505] p-4">
        <div>
          <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">EVENT SCOPE</label>
          <select
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            className="h-10 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none min-w-[220px]"
            data-testid="analytics-event-filter"
          >
            <option value="">All events (last N)</option>
            {(events || []).map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title} · {ev.date}
              </option>
            ))}
          </select>
        </div>
        {!eventId && (
          <div>
            <label className="block text-[10px] font-mono tracking-widest text-zinc-500 mb-1">LAST N EVENTS</label>
            <input
              type="number"
              min="1"
              max="50"
              value={lastN}
              onChange={(e) => setLastN(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              className="h-10 w-24 bg-[#050505] border border-zinc-800 text-white px-3 text-sm focus:border-[#B5FF00] focus:outline-none"
              data-testid="analytics-last-n"
            />
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => download('csv')}
            disabled={!!downloading || loading || aggregates.length === 0}
            className="h-10 px-3 border border-zinc-800 text-zinc-300 hover:text-[#B5FF00] hover:border-[#B5FF00] font-mono text-xs tracking-widest flex items-center gap-2 disabled:opacity-40"
            data-testid="analytics-download-csv"
          >
            {downloading === 'csv' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            CSV
          </button>
          <button
            onClick={() => download('pdf')}
            disabled={!!downloading || loading || aggregates.length === 0}
            className="h-10 px-3 border border-[#B5FF00]/40 bg-[#B5FF00]/5 text-[#B5FF00] hover:bg-[#B5FF00]/10 font-mono text-xs tracking-widest flex items-center gap-2 disabled:opacity-40"
            data-testid="analytics-download-pdf"
          >
            {downloading === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            PDF REPORT
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10 text-zinc-500 text-xs font-mono">
          <Loader2 size={14} className="animate-spin mr-2" /> CRUNCHING NUMBERS…
        </div>
      )}

      {!loading && aggregates.length === 0 && (
        <div className="border border-zinc-800 bg-[#050505] p-10 text-center">
          <TrendingUp size={32} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm font-mono tracking-widest">NO PROMOTER ACTIVITY YET</p>
          <p className="text-zinc-600 text-xs mt-2">Analytics will populate once promoters start adding guests to lists under your events.</p>
        </div>
      )}

      {!loading && aggregates.length > 0 && (
        <>
          {/* Hero stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="analytics-hero">
            <StatCard icon={<Users size={14} />} label="PROMOTERS" value={aggregates.length} />
            <StatCard
              icon={<Calendar size={14} />}
              label="EVENTS IN SCOPE"
              value={report?.scope?.event_count ?? 0}
            />
            <StatCard
              icon={<Users size={14} />}
              label="GUESTS DRIVEN"
              value={aggregates.reduce((s, r) => s + (r.guest_total || 0), 0)}
            />
            <StatCard
              icon={<Trophy size={14} />}
              label="TOP PROMOTER"
              value={top ? top.promoter_name : '—'}
              valueClass="text-base truncate"
            />
          </div>

          {/* Aggregate leaderboard */}
          <section data-testid="analytics-aggregate">
            <h3 className="font-mono text-xs text-zinc-400 tracking-widest mb-2">
              LEADERBOARD · ACROSS {report?.scope?.event_count ?? 0} EVENT(S)
            </h3>
            <div className="border border-zinc-800 overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-zinc-800 bg-[#050505]">
                    <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">#</th>
                    <th className="text-left py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">PROMOTER</th>
                    <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">EVENTS</th>
                    <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">GUESTS</th>
                    <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">CONFIRMED</th>
                    <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">CHECKED-IN</th>
                    <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">CONV %</th>
                    <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">TIX SOLD</th>
                    <th className="text-right py-2 px-3 font-mono text-[10px] text-zinc-500 tracking-widest">REVENUE</th>
                  </tr>
                </thead>
                <tbody>
                  {aggregates.map((r, i) => (
                    <tr key={r.promoter_id} className="border-b border-zinc-900 hover:bg-[#050505]" data-testid={`analytics-agg-row-${r.promoter_id}`}>
                      <td className="py-2 px-3 text-zinc-500 font-mono text-xs">{i + 1}</td>
                      <td className="py-2 px-3">
                        <div className="font-['Josefin_Sans'] text-white">{r.promoter_name}</div>
                        <div className="font-mono text-[10px] text-zinc-500">{r.promoter_email}</div>
                      </td>
                      <td className="py-2 px-3 text-right text-zinc-300 font-mono text-xs">{r.events_worked}</td>
                      <td className="py-2 px-3 text-right text-white font-mono">{r.guest_total}</td>
                      <td className="py-2 px-3 text-right text-zinc-300 font-mono">{r.confirmed}</td>
                      <td className="py-2 px-3 text-right text-[#B5FF00] font-mono">{r.checked_in}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs">
                        <span className={r.conversion_pct >= 60 ? 'text-[#B5FF00]' : r.conversion_pct >= 30 ? 'text-amber-400' : 'text-zinc-400'}>
                          {r.conversion_pct}%
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right text-zinc-300 font-mono">{r.attributed_tickets_sold}</td>
                      <td className="py-2 px-3 text-right text-white font-mono text-xs">
                        {r.attributed_revenue > 0 ? `₹${Number(r.attributed_revenue).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Per-event breakdown */}
          {perEvent.length > 1 && (
            <section data-testid="analytics-per-event">
              <h3 className="font-mono text-xs text-zinc-400 tracking-widest mb-2 flex items-center gap-2">
                <TrendingUp size={12} /> TREND · PER EVENT
              </h3>
              <div className="space-y-4">
                {perEvent.map((ev) => (
                  <div key={ev.event_id} className="border border-zinc-800" data-testid={`analytics-event-${ev.event_id}`}>
                    <div className="bg-[#050505] py-2 px-3 border-b border-zinc-800 flex items-center justify-between gap-3">
                      <div>
                        <div className="font-['Josefin_Sans'] text-white text-sm">{ev.event_title}</div>
                        <div className="font-mono text-[10px] text-zinc-500">{ev.event_date} · {ev.venue}</div>
                      </div>
                      <div className="font-mono text-[10px] text-zinc-400">
                        {ev.promoters.length} promoter{ev.promoters.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    {ev.promoters.length === 0 ? (
                      <p className="text-zinc-500 text-xs p-4">No promoter activity for this event.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-zinc-900 bg-[#030303]">
                            <th className="text-left py-1.5 px-3 font-mono text-[10px] text-zinc-600 tracking-widest">PROMOTER</th>
                            <th className="text-right py-1.5 px-3 font-mono text-[10px] text-zinc-600 tracking-widest">LISTS</th>
                            <th className="text-right py-1.5 px-3 font-mono text-[10px] text-zinc-600 tracking-widest">GUESTS</th>
                            <th className="text-right py-1.5 px-3 font-mono text-[10px] text-zinc-600 tracking-widest">CONFIRMED</th>
                            <th className="text-right py-1.5 px-3 font-mono text-[10px] text-zinc-600 tracking-widest">CHECKED-IN</th>
                            <th className="text-right py-1.5 px-3 font-mono text-[10px] text-zinc-600 tracking-widest">CONV %</th>
                            <th className="text-right py-1.5 px-3 font-mono text-[10px] text-zinc-600 tracking-widest">TIX SOLD</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ev.promoters.map((r) => (
                            <tr key={r.promoter_id} className="border-b border-zinc-900">
                              <td className="py-1.5 px-3 text-white">{r.promoter_name}</td>
                              <td className="py-1.5 px-3 text-right text-zinc-400 font-mono text-xs">{r.list_count}</td>
                              <td className="py-1.5 px-3 text-right text-white font-mono">{r.guest_total}</td>
                              <td className="py-1.5 px-3 text-right text-zinc-300 font-mono">{r.confirmed}</td>
                              <td className="py-1.5 px-3 text-right text-[#B5FF00] font-mono">{r.checked_in}</td>
                              <td className="py-1.5 px-3 text-right font-mono text-xs">
                                <span className={r.conversion_pct >= 60 ? 'text-[#B5FF00]' : r.conversion_pct >= 30 ? 'text-amber-400' : 'text-zinc-400'}>
                                  {r.conversion_pct}%
                                </span>
                              </td>
                              <td className="py-1.5 px-3 text-right text-zinc-300 font-mono">{r.attributed_tickets_sold}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, valueClass = '' }) {
  return (
    <div className="border border-zinc-800 bg-[#050505] p-4">
      <div className="flex items-center gap-2 text-zinc-500">
        {icon}
        <span className="font-mono text-[10px] tracking-widest">{label}</span>
      </div>
      <div className={`text-3xl text-white font-['Josefin_Sans'] mt-1 ${valueClass}`}>{value}</div>
    </div>
  );
}
